import React, { useContext, useState, useEffect, useMemo } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	IGetDishRestaurantProperties,
	IGetDishRestaurantPropertiesArguments,
	IGetCategoriesShortResponse,
	IGetCategoriesShort,
	IGetDishInfoV2,
	IAssignDishToSubcategories,
} from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { convertUndefinedFields } from 'utils/helpers';
import { getCategoriesShortInfoV2 } from 'backend/categories';
import { getDishInfoV2, getDishRestaurantProperties, assignDishToSubcategories } from 'backend/dishes';
import { Form } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { preventDefaultForEnterKey } from 'utils/helpers';
import { ModalContent } from 'components/ModalWindows/ModalContent';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import Text from 'components/Text/Text';
import { CategorySubcategoryBlock } from './CategorySubcategoryBlock';
import Button from 'components/Button/Button';
import breadcrumbs from 'images/breadcrumbs.svg';
import style from './index.module.scss';

interface ISelectedCategorySubcategory {
	index: number;
	selectedCategoryId: Nullable<string>;
	uniqueId: string;
	selectedSubcategoryIds: string[];
}

interface IProps {
	onCloseModal: () => void;
	onFailureEvent?: () => void;
	onShowError: () => void;
	dishId: string;
	IDSelectedRestaurant: string;
}

export const ModalAssignDishToSubcategoryOnRestaurant: React.FC<IProps> = ({
	onCloseModal,
	onFailureEvent,
	onShowError,
	dishId,
	IDSelectedRestaurant,
	// categories,
}) => {
	const [form] = Form.useForm();
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [selectedCategoriesSubcategories, setSelectedCategoriesSubcategories] = useState<
		ISelectedCategorySubcategory[]
	>([]);
	const { response: dishInfoResponse, loading: dishInfoLoading } = useFetchData<IGetDishInfoV2, string>(
		getDishInfoV2,
		dishId,
	);
	const { response: restaurantPropertiesResponse, loading: restaurantPropertiesLoading } = useFetchData<
		IGetDishRestaurantProperties,
		IGetDishRestaurantPropertiesArguments
	>(getDishRestaurantProperties, {
		restaurantId: IDSelectedRestaurant,
		dishId,
	});
	const { response: allCategoriesResponse, loading: allCategoriesLoading } = useFetchData<
		IGetCategoriesShortResponse,
		IGetCategoriesShort
	>(getCategoriesShortInfoV2, { restaurantIds: [IDSelectedRestaurant] });

	useEffect(() => {
		dishInfoLoading || restaurantPropertiesLoading || allCategoriesLoading ? showLoader() : hideLoader();
	}, [dishInfoLoading, restaurantPropertiesLoading, allCategoriesLoading]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			const categoriesSubcategories = restaurantPropertiesResponse.data.categories.map((category, index) => ({
				index,
				selectedCategoryId: category.id,
				uniqueId: uuidv4(),
				selectedSubcategoryIds: category.subCategories.map((subCategory) => subCategory.id),
			}));
			setSelectedCategoriesSubcategories(categoriesSubcategories);
		}
	}, [restaurantPropertiesResponse]);

	const restaurantName = useMemo(() => {
		return dishInfoResponse
			? dishInfoResponse?.data.restaurants.find((restaurant) => restaurant.id === IDSelectedRestaurant)?.name
			: '-';
	}, [dishInfoResponse]);

	useEffect(() => {
		selectedCategoriesSubcategories.forEach((el) => {
			form.setFieldsValue({
				[`IDSelectedCategory-${el.index}`]: el.selectedCategoryId,
				[`IDsSelectedSubcategories-${el.index}`]: el.selectedSubcategoryIds,
			});
		});
	}, [selectedCategoriesSubcategories]);

	const submit = async () => {
		try {
			const assignments: IAssignDishToSubcategories['assignments'] = selectedCategoriesSubcategories.map(
				(categorySubcategory) => ({
					categoryId: categorySubcategory.selectedCategoryId || '',
					subCategoryIds: categorySubcategory.selectedSubcategoryIds,
				}),
			);
			const convertedData: IAssignDishToSubcategories = convertUndefinedFields({
				dishId,
				restaurantIds: [IDSelectedRestaurant],
				assignments,
			});

			showLoader();
			await assignDishToSubcategories(convertedData);
			onCloseModal();
		} catch (err) {
			onShowError();
		} finally {
			hideLoader();
		}
	};

	const handleAddCategory = () => {
		setSelectedCategoriesSubcategories((state) => [
			...state,
			{ index: state.length, selectedCategoryId: null, uniqueId: uuidv4(), selectedSubcategoryIds: [] },
		]);
	};

	return (
		<ModalContent
			onConsentEvent={form.submit}
			onFailureEvent={onFailureEvent ? onFailureEvent : onCloseModal}
			heading="editCategorySubcategoryList"
			headerTranslationNameSpace="modals"
			translationOfConfirmBtn="saveButton"
			headingAlignment="left"
			centered={false}>
			<Text translationKey="descriptionForEditCategorySubcategoryModal" translationNameSpace="modals" size="s" />
			<div className={style.assignInfo}>
				<div>
					<FullInfoField label="customerLabel" />
					<Text translationKey={dishInfoResponse?.data.customer.name || '-'} size="s" withTooltip={true} />
				</div>
				<div>
					<FullInfoField label="restaurantLabel" />
					<Text translationKey={restaurantName || '-'} size="s" withTooltip={true} />
				</div>
				<div>
					<FullInfoField label="dishLabel" />
					<Text translationKey={dishInfoResponse?.data.name || '-'} size="s" withTooltip={true} />
				</div>
			</div>
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				{!!selectedCategoriesSubcategories.length &&
					allCategoriesResponse &&
					selectedCategoriesSubcategories.map((selectedCategorySubcategory) => (
						<CategorySubcategoryBlock
							key={selectedCategorySubcategory.uniqueId}
							setSelectedCategoriesSubcategories={setSelectedCategoriesSubcategories}
							fetchedCategories={allCategoriesResponse.data.categories}
							selectedCategoriesSubcategories={selectedCategoriesSubcategories}
							selectedCategorySubcategory={selectedCategorySubcategory}
							IDSelectedRestaurant={IDSelectedRestaurant}
							isLoading={allCategoriesLoading}
						/>
					))}
				{selectedCategoriesSubcategories.length < (allCategoriesResponse?.data.categories.length || 0) && (
					<Button
						className={style.editBtn}
						size="sizeS"
						theme="outlined"
						onClick={handleAddCategory}
						icon={
							<svg className={style.icon}>
								<use xlinkHref={`${breadcrumbs}#plus`} />
							</svg>
						}>
						<Text translationNameSpace="modals" translationKey="addCategoryButton" asFragment={true} />
					</Button>
				)}
			</Form>
		</ModalContent>
	);
};

export { ISelectedCategorySubcategory };
